import React, {forwardRef, useImperativeHandle, useRef} from 'react'
import {useAppConfig} from '../../../context/app/AppContext'
import {useAuth} from '../../authentication/AuthProvider'
import {component} from '../../form/Constant'
import {
    useListHospitals,
    useSectionListHospitals,
} from '../../hospital/query-keys/HospitalQueryManager'
import StyledDropdown from '../components/StyledDropdown'
import StyledMultiSelect from '../components/StyledMultiSelect'

//groupByIsNull
// Null means no grouping - get all hospitals
// True means group by null - get all hospitals that are not grouped
// False means group by not null - get all hospitals that are grouped

const StyledHospitalOptions = forwardRef(
    (
        {
            value = null,
            onChange = () => {},
            placeholder = 'Select Hospital',

            hasPanelFooter = true,
            optionType = component.dropdownGroup,
            groupByIsNull = null,
            disabled = false,

            excludeGroupById = null, // Exclude Group By Id itself in Hospital Form
        },
        ref,
    ) => {
        useImperativeHandle(ref, () => ({
            getValue: () => handleGetValue(),
            changeValue: newValue => handleChangeValue(newValue),
        }))

        //#region Context
        const {user} = useAuth()
        const {userRole, hospitalBelongs} = useAppConfig()
        //#endregion

        //#region Value
        const hospitalOptionRef = useRef(null)

        const handleGetValue = () => {
            return hospitalOptionRef.current?.getValue()
        }

        const handleChangeValue = newValue => {
            hospitalOptionRef.current?.changeValue(newValue)
        }
        //#endregion

        //#region Dropdown
        const {data: hospitalOptions} = useListHospitals(
            user?.id,
            true,
            {groupByIsNull: groupByIsNull, excludeId: excludeGroupById},
            optionType === component.dropdown,
        )

        const valueTemplate = (option, props) => {
            if (option) {
                return (
                    <div className="flex align-items-center">
                        {option.data ? (
                            <img
                                alt={option.label}
                                src={option.data}
                                className={`mr-2`}
                                style={{width: '48px', height: '20px'}}
                            />
                        ) : (
                            <i
                                className="pi pi-home mr-2"
                                style={{fontSize: '16px'}}></i>
                        )}

                        <div className="font-bold">{option.label}</div>
                    </div>
                )
            }

            return <span>{props.placeholder}</span>
        }

        const itemTemplate = option => {
            return (
                <div className="flex align-items-center">
                    <div
                        className="flex align-items-center justify-content-center mr-2"
                        style={{width: '48px'}}>
                        {option.data ? (
                            <img
                                alt={option.label}
                                src={option.data}
                                className={`mr-2`}
                                style={{width: '100%', height: '24px'}}
                            />
                        ) : (
                            <div>
                                <i
                                    className="pi pi-home"
                                    style={{fontSize: '20px'}}></i>
                            </div>
                        )}
                    </div>
                    <div className="font-bold">{option.label}</div>
                </div>
            )
        }
        //#endregion

        //#region MultiSelect
        const {data: hospitalSectionOptions} = useSectionListHospitals(
            user?.id,
            {groupByIsNull: groupByIsNull, excludeId: excludeGroupById},
            optionType === component.multiSelect ||
                optionType === component.dropdownGroup,
        )

        const multiSelectGroupItemTemplate = option => {
            return (
                <div className="flex align-items-center">
                    <div>
                        Group By
                        <span className="text-darkcyan">
                            {` `} {option.label} ({option?.items?.length}{' '}
                            options)
                        </span>
                    </div>
                </div>
            )
        }

        const multiSelectItemTemplate = option => {
            return (
                <div className="flex align-items-center">
                    <div
                        className="flex align-items-center justify-content-center mr-2"
                        style={{width: '48px'}}>
                        {option.data ? (
                            <img
                                alt={option.label}
                                src={option.data}
                                className={`mr-2`}
                                style={{width: '100%', height: '24px'}}
                            />
                        ) : (
                            <div>
                                <i
                                    className="pi pi-home"
                                    style={{fontSize: '20px'}}></i>
                            </div>
                        )}
                    </div>
                    <div className="font-bold">{option.label}</div>
                </div>
            )
        }
        //#endregion

        return (
            <React.Fragment>
                {optionType === component.dropdown && (
                    <StyledDropdown
                        ref={hospitalOptionRef}
                        options={hospitalOptions || []}
                        value={value}
                        onChange={onChange}
                        placeholder={placeholder}
                        itemTemplate={itemTemplate}
                        valueTemplate={valueTemplate}
                        showClear={true}
                        hasPanelFooter={hasPanelFooter}
                        optionType={component.dropdown}
                        disabled={disabled}
                    />
                )}

                {optionType === component.dropdownGroup && (
                    <StyledDropdown
                        ref={hospitalOptionRef}
                        options={hospitalSectionOptions || []}
                        value={value}
                        onChange={onChange}
                        placeholder={placeholder}
                        itemTemplate={itemTemplate}
                        valueTemplate={valueTemplate}
                        showClear={true}
                        optionLabel="label"
                        optionGroupLabel="label"
                        optionGroupChildren="items"
                        optionGroupTemplate={multiSelectGroupItemTemplate}
                        hasPanelFooter={hasPanelFooter}
                        optionType={component.dropdownGroup}
                        disabled={disabled}
                    />
                )}

                {optionType === component.multiSelect && (
                    <StyledMultiSelect
                        ref={hospitalOptionRef}
                        options={hospitalSectionOptions || []}
                        value={value}
                        onChange={onChange}
                        placeholder={placeholder}
                        itemTemplate={multiSelectItemTemplate}
                        optionGroupTemplate={multiSelectGroupItemTemplate}
                        optionGroupLabel="label"
                        optionGroupChildren="items"
                        showClear={true}
                        disabled={disabled}
                    />
                )}

                {/* {userRole === SystemRole.ADMIN && (
                    <p className="my-2">
                        <i className="pi pi-info-circle mr-2"></i>
                        <span>
                            You are seeing all hospitals because you are an
                            <strong> Admin</strong>
                        </span>
                    </p>
                )} */}

                {/* {userRole === SystemRole.USER_WITH_ROLE && (
                    <p className="my-2">
                        <i className="pi pi-info-circle mr-2"></i>
                        <span>
                            You can only see hospitals that are assigned to you
                        </span>
                    </p>
                )} */}
            </React.Fragment>
        )
    },
)

export default StyledHospitalOptions
